import { Static, Type } from '@sinclair/typebox';
import { PriceOptionResponseSchema, resolveSupplementQuantity } from './price';

/**
 * Ré-export : `computeBillableUnits` vit dans `price.ts` (seul module que `catalog-selection`
 * importe déjà) pour que la facturation d'une ligne reste au même endroit que le calcul des
 * totaux, sans créer de cycle d'import entre les deux fichiers.
 */
export { computeBillableUnits } from './price';

export const SupplementOptionSchema = Type.Object({
  supplement_id: Type.Number(),
  name: Type.String(),
  price: Type.Optional(Type.Number()),
  price_string: Type.Optional(
    Type.String({
      meta: { is_price: true },
    }),
  ),
  duration_minutes: Type.Optional(Type.Number()),
  /** Se compte au choix (ni durée ni salle) : renseigné quand la salle du supplément est connue. */
  quantity_selectable: Type.Optional(Type.Boolean()),
});

/**
 * Quantité choisie pour un supplément sélectionné (cf. `resolveSupplementQuantity`). Un supplément
 * sans entrée suit la règle historique : une unité par unité facturée. Le plafond dépend de la ligne
 * (`resolveSupplementQuantityMax`), vérifié par le backend.
 */
export const SupplementQuantitySchema = Type.Object({
  supplement_id: Type.Integer(),
  quantity: Type.Integer({ minimum: 1 }),
});

export const SupplementQuantitiesSchema = Type.Array(SupplementQuantitySchema);

export const CatalogSelectionInputSchema = Type.Object({
  prestation_id: Type.Number(),
  quantity: Type.Integer({ minimum: 1 }),
  selected_price_index: Type.Optional(Type.Integer({ minimum: 0 })),
  // Formule désignée par son identifiant stable (panier du site, tunnel) : prime sur l'index quand elle existe
  // encore ; l'index reste accepté (ancien bundle).
  price_id: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_duration_minutes: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  supplement_quantities: Type.Optional(SupplementQuantitiesSchema),
  selected_resource_id: Type.Optional(Type.Integer()),
  duration: Type.Optional(Type.Number()),
  price: Type.Optional(Type.Number()),
  party_size: Type.Optional(Type.Integer({ minimum: 1 })),
});

export type CatalogSelectionInputObject = Static<typeof CatalogSelectionInputSchema>;

/**
 * Champs de catalogue qui décident si le client peut choisir une taille de groupe, et jusqu'où.
 * Volontairement structurel (pas de type Prisma / TypeBox) : les mêmes bornes voyagent sous
 * plusieurs formes — ligne Prisma (`null` possible), prestation du tunnel, prestation publique
 * d'un site vitrine.
 */
export interface PartySizeCapacityFields {
  is_party_size_open?: boolean | null;
  clients_per_booking_max?: number | null;
  max_session_capacity?: number | null;
}

/** Plafond retenu quand la prestation n'annonce AUCUNE borne (ni par réservation, ni par séance). */
export const PARTY_SIZE_UNBOUNDED_MAX = 99;

/**
 * Plafond de taille de groupe proposable : la plus CONTRAIGNANTE des deux bornes renseignées
 * (`clients_per_booking_max` = personnes par réservation, `max_session_capacity` = places de la
 * séance). Miroir CLIENT de la garde serveur `_assertPartySizeWithinBounds` : proposer au-delà
 * ferait échouer le checkout après que le client a tout saisi.
 */
export const resolvePartySizeMax = (prestation: PartySizeCapacityFields): number => {
  const perBooking = prestation.clients_per_booking_max ?? undefined;
  const perSession = prestation.max_session_capacity ?? undefined;
  if (perBooking && perSession) return Math.min(perBooking, perSession);
  return perBooking ?? perSession ?? PARTY_SIZE_UNBOUNDED_MAX;
};

/**
 * Vrai ⟺ afficher un sélecteur « nombre de personnes » a un sens : le pro l'a ouvert ET le
 * plafond laisse un choix. Plafond à 1 (préset Libre-service par défaut, séance dont chaque
 * réservation vaut une place) ⇒ masqué, même flag actif : un choix borné à 1 n'en est pas un.
 */
export const isPartySizeSelectable = (prestation: PartySizeCapacityFields): boolean =>
  prestation.is_party_size_open === true && resolvePartySizeMax(prestation) > 1;

/**
 * Ramène une taille de groupe dans [1, max] : saisie clavier, valeur restaurée d'un panier
 * localStorage périmé (le pro a baissé son plafond depuis) ou forgée à la main.
 */
export const clampPartySize = (value: number | null | undefined, max: number): number => {
  if (!value || !Number.isFinite(value)) return 1;
  return Math.min(Math.max(Math.trunc(value), 1), Math.max(1, max));
};

export const CatalogSelectionResponseSchema = Type.Object({
  prestation_id: Type.Number(),
  name: Type.String(),
  color: Type.Optional(Type.String()),
  /** Catégorie de catalogue (pour regrouper/filtrer les prestations, ex. tunnel carte cadeau). */
  category_id: Type.Optional(Type.Number()),
  quantity: Type.Integer({ minimum: 1 }),
  /* Nombre de personnes facturées sur la ligne. Renseigné seulement au-delà d'une unité — c'est
     le multiplicateur appliqué au prix, que l'affichage doit rendre visible (sinon un total
     quadruplé apparaît sans explication). */
  party_size: Type.Optional(Type.Integer({ minimum: 2 })),
  /* Nombre BRUT de personnes stocké sur la ligne. Absent uniquement pour une ligne legacy dont
     `appointment_prestation.party_size` est NULL. */
  party_size_raw: Type.Optional(Type.Integer({ minimum: 1 })),
  /* Total de la ligne (prix unitaire + suppléments) × unités facturées, formaté serveur via
     `transformToSchema` (clé source `line_total`). Ne sort qu'avec `party_size`. */
  line_total_string: Type.Optional(Type.String({ meta: { is_price: true } })),
  position: Type.Optional(Type.Integer()),
  selected_price_index: Type.Optional(Type.Integer({ minimum: 0 })),
  selected_duration_minutes: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  supplement_quantities: Type.Optional(SupplementQuantitiesSchema),
  prices: Type.Optional(Type.Array(PriceOptionResponseSchema)),
  supplements: Type.Optional(Type.Array(SupplementOptionSchema)),
  /* Flag du catalogue vivant, utilisé pour identifier une séance et ses règles de capacité. */
  is_session: Type.Optional(Type.Boolean()),
  /*
   * Séance de groupe : capacité effective de la séance = MIN(max_session_capacity VIVANT,
   * MIN(capacity des ressources non-POOL VIVANTES compatibles avec le lieu)). Dénominateur du
   * badge agenda « 4/10 ». Valeurs du catalogue VIVANT, dérogation assumée à la règle snapshot :
   * ce cap régit la JOIGNABILITÉ FUTURE de la séance (moteur de dispo et checkout lisent les
   * valeurs vives) — afficher le cap figé contredirait le comportement réel du badge. Renseigné
   * uniquement par l'agenda (liste + détail) ; absent ailleurs et sur les séances non plafonnées.
   */
  session_effective_capacity: Type.Optional(Type.Integer({ minimum: 1 })),
});

export type CatalogSelectionResponseObject = Static<typeof CatalogSelectionResponseSchema>;

/** Ligne de prestation telle que le client la voit : page de paiement et email de demande de paiement. */
export interface CatalogSelectionDisplayLine {
  prestation_id: number;
  name: string;
  quantity: number;
  /** Prix de l'option retenue, déjà formaté côté serveur. */
  price?: string;
  /** Suppléments retenus, « Nom × quantité (prix unitaire) » : quantité au-delà d'une unité, prix s'il est connu. */
  supplements: string[];
}

/**
 * Lignes affichables d'une sélection de catalogue formatée par le backend (`transformToSchema`). Source
 * unique de la page de paiement et de l'email PAYMENT_REQUEST : les deux montrent la même chose au client.
 */
export const toCatalogSelectionDisplayLines = (
  prestations: CatalogSelectionResponseObject[],
): CatalogSelectionDisplayLine[] =>
  prestations.map((p): CatalogSelectionDisplayLine => {
    const selectedSupplementIds = new Set(p.selected_supplement_ids ?? []);
    // Unités facturées de la ligne : nombre BRUT de personnes (absent sur une ligne legacy).
    const line = { quantity: p.quantity, party_size: p.party_size_raw, supplement_quantities: p.supplement_quantities };
    return {
      prestation_id: p.prestation_id,
      name: p.name,
      quantity: p.quantity,
      price: p.prices?.[p.selected_price_index ?? 0]?.price_infos?.price_string,
      supplements: (p.supplements ?? [])
        .filter((s): boolean => selectedSupplementIds.has(s.supplement_id))
        .map((s): string => {
          const quantity = resolveSupplementQuantity(line, s.supplement_id);
          return `${s.name}${quantity > 1 ? ` × ${quantity}` : ''}${s.price_string ? ` (${s.price_string})` : ''}`;
        }),
    };
  });
